import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { create } from 'zustand';

import {
  SUPABASE_PROVIDERS,
  SupabaseProviderConfig,
  SupabaseProviderId,
} from '../auth/supabase-provider-registry';

import { StorageService } from './storage.service';

interface SupabaseAuthProviderServiceState {
  loadedProviderId: SupabaseProviderId | null;
  client: SupabaseClient | null;
  loading: boolean;
  setLoadedProviderId: (loadedProviderId: SupabaseProviderId) => void;
  setClient: (client: SupabaseClient) => void;
  setLoading: (loading: boolean) => void;
}

export const useSupabaseAuthProviderService =
  create<SupabaseAuthProviderServiceState>(set => ({
    loadedProviderId: null,
    client: null,
    loading: true,
    setLoadedProviderId: (loadedProviderId: SupabaseProviderId) =>
      set({ loadedProviderId }),
    setLoading: (loading: boolean) => set({ loading }),
    setClient: (client: SupabaseClient) => set({ client }),
  }));

class SupabaseAuthProviderService {
  constructor(private readonly providers: SupabaseProviderConfig[]) {}

  init() {
    useSupabaseAuthProviderService.getState().setLoading(true);

    const client = useSupabaseAuthProviderService.getState().client;

    if (client) {
      return;
    }

    const storedProviderId = StorageService.usedSupabaseProviderId.get();

    if (storedProviderId) {
      this.loadProviderById(storedProviderId as SupabaseProviderId);
    } else {
      // TODO: check if this will work
      this.loadDefaultProvider();
    }

    useSupabaseAuthProviderService.getState().setLoading(false);
  }

  get client(): SupabaseClient | null {
    const client = useSupabaseAuthProviderService.getState().client;

    // TODO: throw or not??
    // if (!this.clientInstance) {
    //   throw new Error('Client not loaded');
    // }

    return client;
  }

  private createClient(provider: SupabaseProviderConfig) {
    return createClient(provider.supabaseUrl, provider.anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
      },
    });
  }

  hasProviderById(id: SupabaseProviderId) {
    return this.providers.some(provider => provider.id === id);
  }

  loadProviderById(id: SupabaseProviderId, fallbackToDefault = false) {
    let provider = this.providers.find(provider => provider.id === id);

    if (!provider) {
      if (fallbackToDefault) {
        const defaultProvider = this.providers.find(
          provider => provider.isDefault,
        );

        if (!defaultProvider) {
          throw new Error('Default provider not found');
        }

        provider = defaultProvider;
      } else {
        // TODO: Should we throw or fallback to clearing the persisted provider if matched by it????
        // throw new Error('Provider not found');
        this.clearPersistedProvider();

        return;
      }
    }

    useSupabaseAuthProviderService.getState().setLoadedProviderId(provider.id);
    const client = this.createClient(provider);
    useSupabaseAuthProviderService.getState().setClient(client);

    return provider;
  }

  hasProviderByDomain(domain: string) {
    return this.providers.some(provider =>
      provider.ssoValidDomains?.includes(domain),
    );
  }

  getProviderByDomain(domain: string): SupabaseProviderConfig {
    const provider = this.providers.find(provider =>
      provider.ssoValidDomains?.includes(domain),
    );

    if (!provider) {
      throw new Error(`Provider for domain ${domain} not found`);
    }

    return provider;
  }

  loadProviderByDomain(domain: string, fallbackToDefault = false) {
    let provider = this.providers.find(provider =>
      provider.ssoValidDomains?.includes(domain),
    );

    if (!provider) {
      if (fallbackToDefault) {
        const defaultProvider = this.providers.find(
          provider => provider.isDefault,
        );

        if (!defaultProvider) {
          throw new Error('Default provider not found');
        }

        provider = defaultProvider;
      } else {
        throw new Error('Provider not found');
      }
    }

    useSupabaseAuthProviderService.getState().setLoadedProviderId(provider.id);
    const client = this.createClient(provider);
    useSupabaseAuthProviderService.getState().setClient(client);

    return provider;
  }

  loadDefaultProvider() {
    const defaultProvider = this.providers.find(provider => provider.isDefault);

    if (!defaultProvider) {
      throw new Error('Default provider not found');
    }

    useSupabaseAuthProviderService
      .getState()
      .setLoadedProviderId(defaultProvider.id);
    const client = this.createClient(defaultProvider);
    useSupabaseAuthProviderService.getState().setClient(client);

    return defaultProvider;
  }

  get loadedProvider(): SupabaseProviderConfig | null {
    const loadedProviderId =
      useSupabaseAuthProviderService.getState().loadedProviderId;
    const loadedProvider = this.providers.find(
      provider => provider.id === loadedProviderId,
    );

    if (!loadedProvider) {
      return null;
    }

    return loadedProvider;
  }

  persistLoadedProvider() {
    const loadedProviderId =
      useSupabaseAuthProviderService.getState().loadedProviderId;

    if (!loadedProviderId) {
      throw new Error('Client not loaded');
    }

    StorageService.usedSupabaseProviderId.set(loadedProviderId);
  }

  loadPersistedProvider() {
    const storedProviderId = StorageService.usedSupabaseProviderId.get();

    if (storedProviderId) {
      this.loadProviderById(storedProviderId as SupabaseProviderId);
    } else {
      // TODO: add debug logging, no provider persisted in storage
    }
  }

  clearPersistedProvider() {
    return StorageService.usedSupabaseProviderId.remove();
  }
}

export default new SupabaseAuthProviderService(SUPABASE_PROVIDERS);
